import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export type ThemeMode = 'dark' | 'light' | 'system'

function getSystemTheme(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getEffectiveTheme(mode: ThemeMode): 'dark' | 'light' {
  if (mode === 'system') return getSystemTheme()
  return mode
}

export const useThemeStore = defineStore('theme', () => {
  const stored = localStorage.getItem('theme') as ThemeMode | null
  const mode = ref<ThemeMode>(stored || 'dark')

  function applyTheme() {
    const effective = getEffectiveTheme(mode.value)
    const html = document.documentElement
    if (effective === 'dark') {
      html.classList.add('dark')
      html.classList.remove('light')
    } else {
      html.classList.add('light')
      html.classList.remove('dark')
    }
  }

  function setTheme(newMode: ThemeMode) {
    mode.value = newMode
    localStorage.setItem('theme', newMode)
    applyTheme()
  }

  function toggle() {
    setTheme(mode.value === 'dark' ? 'light' : 'dark')
  }

  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (mode.value === 'system') applyTheme()
  })

  // Apply on init
  applyTheme()

  watch(mode, applyTheme)

  return { mode, setTheme, toggle, applyTheme }
})
